// frontend/src/components/forms/PetForm.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Pet,
  Client,
  PetType,
  PetSize,
  PetBreed,
  PetColour,
  petsAPI,
  referenceDataAPI,
} from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import CustomSelectMenuItem from '../common/CustomSelectMenuItem';
import { CancelFormButton, SaveFormButton } from '../common/Buttons';
import DateRangePicker from './shared/DateRangePicker';
import {
  FormWrapper,
  FormContent,
  StickyFooter,
  StickyButtonContainer,
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  StyledTextField,
  StyledMultilineTextField,
  ResponsiveFormGrid,
  FieldLabelContainerMultiline,
  StyledSwitch,
  RadioButtonGroup,
  SimilarityDialog,
  TopSpacerRow,
  BottomSpacerRow,
} from './shared';
import DangerZoneRow from './shared/DangerZoneRow';
import DeleteConfirmDialog from './shared/modal/DeleteConfirmDialog';
import { useGlobalToast } from '../../App';
import {
  formValidators,
  isFormValid,
  FormErrors,
  scrollToFirstError,
} from '../../utils/validationHelpers';
import { findFirstSimilarPet } from '../../utils/petSimilarityHelper';
import { colors } from '../../theme/theme';
import LoadingSpinner from '../common/LoadingSpinner';
import { useEnterNavigation } from '../../hooks/useEnterNavigation';

interface PetFormData {
  name: string;
  petTypeId: string;
  petSizeId: string;
  petBreedId: string;
  petColourId: string;
  dateOfBirth: string;
  gender: string;
  notes: string;
  stillOwned: boolean;
  showWarning: boolean;
  inoculationDate: string;
  sterilised: boolean;
}

type PetFormErrors = FormErrors<PetFormData>;

// Values for the "Show All"/"Show Relevant" last option in Breed and Colour.
// Intercepted in each select's onChange before the normal field handler.
const BREED_SHOW_ALL_TOGGLE_VALUE = '__toggle_show_all_breeds__';
const COLOUR_SHOW_ALL_TOGGLE_VALUE = '__toggle_show_all_colours__';

interface PetFormProps {
  client: Client;
  pet?: Pet;
  onSuccess: (result: {
    clientId: number;
    clientName: string;
    petId?: number;
    isEditMode: boolean;
  }) => void;
  onCancel: () => void;
  title?: string;
  alternateRowColors?: boolean;
  scrollToFirstError?: boolean;
}

const formatDateForInput = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  const dateOnly = isoDate.split('T')[0];
  return dateOnly;
};

const validateRequiredClient = (client: Client | undefined): Client => {
  if (!client) {
    throw new Error(
      'PetForm Error: Client context is required. ' +
        'Pets cannot be created or edited without owner information. ' +
        'Ensure PetForm is called with a valid client prop.'
    );
  }
  return client;
};

const PetForm: React.FC<PetFormProps> = ({
  client: propClient,
  pet,
  onSuccess,
  onCancel,
  title,
  scrollToFirstError: enableScrollToError = true,
}) => {
  const client = validateRequiredClient(propClient);
  const { showSuccess, showError } = useGlobalToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditMode = Boolean(pet);
  const isLoadingReferenceData = useRef(false);
  const isLoadingDependentData = useRef(false);
  const initialLoadComplete = useRef(false);

  const [formData, setFormData] = useState<PetFormData>({
    name: '',
    petTypeId: '',
    petSizeId: '',
    petBreedId: '',
    petColourId: '',
    dateOfBirth: '',
    gender: '',
    notes: '',
    stillOwned: true,
    showWarning: false,
    inoculationDate: '',
    sterilised: false,
  });

  const [errors, setErrors] = useState<PetFormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availablePetTypes, setAvailablePetTypes] = useState<PetType[]>([]);
  const [availablePetSizes, setAvailablePetSizes] = useState<PetSize[]>([]);
  const [availableBreeds, setAvailableBreeds] = useState<PetBreed[]>([]);
  const [availableColours, setAvailableColours] = useState<PetColour[]>([]);
  const [originalPetName, setOriginalPetName] = useState<string>('');
  const [showPetSimilarityDialog, setShowPetSimilarityDialog] = useState(false);
  const [similarPet, setSimilarPet] = useState<Pet | null>(null);
  const [clientPets, setClientPets] = useState<Pet[]>([]);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Breed "all" drops Size scoping, Colour "all" drops Type scoping. Both
  // reset to relative when Type or Size changes.
  const [showAllBreeds, setShowAllBreeds] = useState(false);
  const [showAllColours, setShowAllColours] = useState(false);
  const [isLoadingBreedToggle, setIsLoadingBreedToggle] = useState(false);
  const [isLoadingColourToggle, setIsLoadingColourToggle] = useState(false);

  // Enter-as-Tab order. Size is skipped when shown as text (single size);
  // the date pickers are mouse-only.
  const fieldOrder = useMemo(() => {
    const order: string[] = ['name', 'petTypeId'];
    if (availablePetSizes.length !== 1) {
      order.push('petSizeId');
    }
    order.push(
      'petBreedId',
      'gender',
      'petColourId',
      'sterilised',
      'stillOwned',
      'showWarning',
      'notes',
      'submit'
    );
    return order;
  }, [availablePetSizes.length]);

  const { registerRef, getObjectRef, getKeyHandler, focusFirst } =
    useEnterNavigation(fieldOrder);

  // Refs for scrollToFirstError.
  const fieldRefs = useMemo(() => {
    const map = new Map<keyof PetFormData, React.RefObject<HTMLElement>>();
    (
      [
        'name',
        'petTypeId',
        'petSizeId',
        'petBreedId',
        'gender',
        'notes',
      ] as const
    ).forEach((name) => map.set(name, getObjectRef(name)));
    return map;
  }, [getObjectRef]);

  const sortPetSizesBySize = useCallback((sizes: PetSize[]): PetSize[] => {
    const sizeOrder = [
      'extra small',
      'tiny',
      'small',
      'small/medium',
      'small-medium',
      'medium',
      'medium/large',
      'medium-large',
      'large',
      'extra large',
      'giant',
    ];

    return [...sizes].sort((a, b) => {
      const normalizeSize = (size: string) =>
        size.toLowerCase().trim().replace(/\s+/g, ' ');
      const indexA = sizeOrder.indexOf(normalizeSize(a.size));
      const indexB = sizeOrder.indexOf(normalizeSize(b.size));

      if (indexA === -1 && indexB === -1) return a.size.localeCompare(b.size);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, []);

  const loadDependentData = useCallback(
    async (petTypeId: number): Promise<PetSize[]> => {
      if (isLoadingDependentData.current) {
        return [];
      }

      isLoadingDependentData.current = true;

      try {
        const sizes = await referenceDataAPI.getPetSizesByType(petTypeId);
        setAvailablePetSizes(sizes);
        const colours = await referenceDataAPI.getPetColoursByType(petTypeId);
        setAvailableColours(colours);
        return sizes;
      } catch {
        showError();
        return [];
      } finally {
        isLoadingDependentData.current = false;
      }
    },
    [showError]
  );

  useEffect(() => {
    const loadInitialData = async () => {
      if (initialLoadComplete.current || isLoadingReferenceData.current) {
        return;
      }

      isLoadingReferenceData.current = true;
      setIsLoading(true);

      try {
        const petTypes = await referenceDataAPI.getPetTypes();
        setAvailablePetTypes(petTypes);

        if (client.id) {
          const pets = await petsAPI.getByClient(client.id);
          setClientPets(pets);
        }

        if (isEditMode && pet) {
          setOriginalPetName(pet.name || '');

          setFormData({
            name: pet.name || '',
            petTypeId: pet.petTypeId?.toString() || '',
            petSizeId: pet.petSizeId?.toString() || '',
            petBreedId: pet.petBreedId?.toString() || '',
            petColourId: pet.petColourId?.toString() || '',
            dateOfBirth: formatDateForInput(pet.dateOfBirth),
            gender: pet.gender || '',
            notes: pet.notes || '',
            stillOwned: pet.stillOwned ?? true,
            showWarning: pet.showWarning ?? false,
            inoculationDate: formatDateForInput(pet.inoculationDate),
            sterilised: pet.sterilised ?? false,
          });
          setRowVersion(pet.rowVersionString ?? pet.rowVersion);

          if (pet.petTypeId) {
            await loadDependentData(pet.petTypeId);
          }

          if (pet.petTypeId && pet.petSizeId) {
            const breeds = await referenceDataAPI.getPetBreedsByTypeAndSize(
              pet.petTypeId,
              pet.petSizeId
            );
            setAvailableBreeds(breeds);
          }
        }

        initialLoadComplete.current = true;
      } catch {
        showError();
      } finally {
        setIsLoading(false);
        isLoadingReferenceData.current = false;
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autofocus Pet Name in create mode once loading finishes.
  useEffect(() => {
    if (isEditMode || isLoading) return;
    const timer = setTimeout(() => {
      focusFirst();
    }, 100);
    return () => clearTimeout(timer);
  }, [isEditMode, isLoading, focusFirst]);

  const handleInputChange = useCallback(
    (field: keyof PetFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof PetFormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      if (field === 'showWarning' && !value && errors.notes) {
        setErrors((prev) => ({ ...prev, notes: undefined }));
      }
    },
    [errors]
  );

  const handlePetTypeChange = useCallback(
    async (value: string) => {
      const petTypeId = parseInt(value);

      setFormData((prev) => ({
        ...prev,
        petTypeId: value,
        petSizeId: '',
        petBreedId: '',
        petColourId: '',
      }));

      setAvailablePetSizes([]);
      setAvailableBreeds([]);
      setAvailableColours([]);
      setShowAllBreeds(false);
      setShowAllColours(false);

      if (petTypeId) {
        const sizes = await loadDependentData(petTypeId);

        if (sizes.length === 1) {
          const singleSize = sizes[0];

          setFormData((prev) => ({
            ...prev,
            petSizeId: singleSize.id.toString(),
          }));

          try {
            const breeds = await referenceDataAPI.getPetBreedsByTypeAndSize(
              petTypeId,
              singleSize.id
            );
            setAvailableBreeds(breeds);
          } catch {
            showError();
          }
        }
      }
    },
    [showError, loadDependentData]
  );

  const handlePetSizeChange = useCallback(
    async (value: string) => {
      const petSizeId = parseInt(value);
      const petTypeId = parseInt(formData.petTypeId);

      setFormData((prev) => ({
        ...prev,
        petSizeId: value,
        petBreedId: '',
      }));

      setAvailableBreeds([]);
      setShowAllBreeds(false);

      if (petTypeId && petSizeId) {
        try {
          const breeds = await referenceDataAPI.getPetBreedsByTypeAndSize(
            petTypeId,
            petSizeId
          );
          setAvailableBreeds(breeds);
        } catch {
          showError();
        }
      }
    },
    [formData.petTypeId, showError]
  );

  // Breed "Show All": Type only. The server already returns active breeds.
  const handleToggleShowAllBreeds = useCallback(async () => {
    const petTypeId = parseInt(formData.petTypeId);
    if (!petTypeId) return;

    if (showAllBreeds) {
      setShowAllBreeds(false);
      const petSizeId = parseInt(formData.petSizeId);
      if (petSizeId) {
        setIsLoadingBreedToggle(true);
        try {
          const breeds = await referenceDataAPI.getPetBreedsByTypeAndSize(
            petTypeId,
            petSizeId
          );
          setAvailableBreeds(breeds);
        } catch {
          showError();
        } finally {
          setIsLoadingBreedToggle(false);
        }
      }
      return;
    }

    setIsLoadingBreedToggle(true);
    try {
      const breeds = await referenceDataAPI.getPetBreedsByType(petTypeId);
      setAvailableBreeds(breeds);
      setShowAllBreeds(true);
    } catch {
      showError();
    } finally {
      setIsLoadingBreedToggle(false);
    }
  }, [formData.petTypeId, formData.petSizeId, showAllBreeds, showError]);

  // Colour "Show All": every type. getPetColours() includes inactive rows
  // (Settings needs them), so they are filtered out here.
  const handleToggleShowAllColours = useCallback(async () => {
    const petTypeId = parseInt(formData.petTypeId);

    if (showAllColours) {
      setShowAllColours(false);
      if (petTypeId) {
        setIsLoadingColourToggle(true);
        try {
          const colours = await referenceDataAPI.getPetColoursByType(petTypeId);
          setAvailableColours(colours);
        } catch {
          showError();
        } finally {
          setIsLoadingColourToggle(false);
        }
      }
      return;
    }

    setIsLoadingColourToggle(true);
    try {
      const colours = await referenceDataAPI.getPetColours();
      const active = colours.filter((c) => c.active !== false);

      // One row per colour name, preferring the row for the pet's own Type.
      const byName = new Map<string, PetColour>();
      for (const colour of active) {
        const key = colour.colour.trim().toLowerCase();
        const existing = byName.get(key);
        if (!existing) {
          byName.set(key, colour);
        } else if (petTypeId && colour.petType === petTypeId) {
          byName.set(key, colour);
        }
      }
      const deduped = Array.from(byName.values()).sort((a, b) =>
        a.colour.localeCompare(b.colour)
      );

      setAvailableColours(deduped);
      setShowAllColours(true);
    } catch {
      showError();
    } finally {
      setIsLoadingColourToggle(false);
    }
  }, [formData.petTypeId, showAllColours, showError]);

  const handleUseExistingPet = () => {
    if (isEditMode && similarPet) {
      setFormData((prev) => ({
        ...prev,
        name: originalPetName,
      }));

      setErrors((prev) => ({
        ...prev,
        name: undefined,
      }));
    } else if (!isEditMode && similarPet) {
      navigate(`/pets/${similarPet.id}`);
      return;
    }

    setShowPetSimilarityDialog(false);
    setSimilarPet(null);
  };

  const handleCreateNewPet = () => {
    setShowPetSimilarityDialog(false);
    setSimilarPet(null);
    proceedWithSubmit();
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: PetFormErrors = {};

    const nameError = formValidators.pet.name(formData.name);
    if (nameError) newErrors.name = nameError;

    const petTypeError = formValidators.pet.petType(formData.petTypeId);
    if (petTypeError) newErrors.petTypeId = petTypeError;

    const petSizeError = formValidators.pet.petSize(formData.petSizeId);
    if (petSizeError) newErrors.petSizeId = petSizeError;

    const genderError = formValidators.pet.gender(formData.gender);
    if (genderError) newErrors.gender = genderError;

    const notesError = formValidators.pet.notes(
      formData.notes,
      formData.showWarning
    );
    if (notesError) newErrors.notes = notesError;

    setErrors(newErrors);

    const hasErrors = !isFormValid(newErrors);
    if (hasErrors && enableScrollToError) {
      scrollToFirstError(newErrors, fieldRefs, 120);
    }

    return !hasErrors;
  }, [formData, enableScrollToError, fieldRefs]);

  const proceedWithSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const basePetData = {
        name: formData.name.trim(),
        petTypeId: parseInt(formData.petTypeId),
        petSizeId: parseInt(formData.petSizeId),
        petBreedId: formData.petBreedId
          ? parseInt(formData.petBreedId)
          : undefined,
        petColourId: formData.petColourId
          ? parseInt(formData.petColourId)
          : undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        gender: formData.gender,
        notes: formData.notes.trim(),
        stillOwned: formData.stillOwned,
        showWarning: formData.showWarning,
        inoculationDate: formData.inoculationDate || undefined,
        sterilised: formData.sterilised,
      };

      let savedPet: Pet;
      let resultPetId: number | undefined;

      if (isEditMode && pet) {
        const updatePetData = {
          id: pet.id,
          clientId: pet.clientId,
          ...basePetData,
          rowVersionString: rowVersion,
        };
        savedPet = await petsAPI.update(pet.id, updatePetData);
        resultPetId = savedPet.id ?? pet.id;
      } else {
        const createPetData = {
          ...basePetData,
          clientId: client.id,
        };
        savedPet = await petsAPI.create(createPetData);
        resultPetId = savedPet.id;
      }

      showSuccess();

      onSuccess({
        clientId: client.id,
        clientName: `${client.firstName} ${client.lastName}`,
        petId: resultPetId,
        isEditMode,
      });
    } catch (error: any) {
      if (error.isConflict) {
        setSubmitError(
          'This pet was modified by another user. Please refresh to see the latest changes.'
        );
        return;
      }
      setSubmitError(
        error.response?.data?.message || 'Failed to save pet. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isEditMode, pet, client, rowVersion, showSuccess, onSuccess]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (!validateForm()) {
        return;
      }

      const nameHasChanged =
        isEditMode && formData.name.trim() !== originalPetName;
      const shouldCheckSimilarity = !isEditMode || nameHasChanged;

      if (shouldCheckSimilarity && formData.name.trim()) {
        const similar = findFirstSimilarPet(formData.name, clientPets, pet?.id);

        if (similar) {
          setSimilarPet(similar);
          setShowPetSimilarityDialog(true);
          return;
        }
      }

      await proceedWithSubmit();
    },
    [
      validateForm,
      formData.name,
      clientPets,
      pet,
      proceedWithSubmit,
      isEditMode,
      originalPetName,
    ]
  );

  const handleDeletePet = async () => {
    if (!pet) return;
    setIsDeleting(true);
    try {
      await petsAPI.delete(pet.id);
      showSuccess();
      navigate(`/clients/${client.id}/pets`);
    } catch {
      showError('Failed to delete pet');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <FormWrapper>
        <LoadingSpinner message="Loading pet form..." minHeight="400px" />
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <FormContent>
        <ResponsiveFormGrid
          breakpointPadding={{
            default: '0.25rem',
            medium: '0.1rem',
            large: '0.1rem',
          }}
          container
          spacing={0}
          alternateRowColors={false}
        >
          <TopSpacerRow datesSelected={true} />
          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="pet-name-label">
                Pet Name<span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledTextField
                id="pet-name"
                inputProps={{ 'aria-labelledby': 'pet-name-label' }}
                fullWidth
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('name', e.target.value)
                }
                disabled={isSubmitting}
                error={Boolean(errors.name)}
                helperText={errors.name}
                inputRef={registerRef('name')}
                onKeyDown={getKeyHandler('name')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>
                Type<span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box>
                <CustomSelect
                  value={formData.petTypeId}
                  onChange={handlePetTypeChange}
                  ariaLabel="Type"
                  error={Boolean(errors.petTypeId)}
                  helperText={errors.petTypeId}
                  placeholder="Select pet type"
                  required
                  disabled={isSubmitting}
                  width="100%"
                  maxWidth="600px"
                  ref={registerRef('petTypeId')}
                  onKeyDown={getKeyHandler('petTypeId')}
                >
                  {availablePetTypes.map((type) => (
                    <CustomSelectMenuItem
                      key={type.id}
                      value={type.id.toString()}
                    >
                      {type.type}
                    </CustomSelectMenuItem>
                  ))}
                </CustomSelect>
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>
                Size<span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box>
                {availablePetSizes.length === 1 ? (
                  <Box>
                    <Typography
                      sx={{
                        px: 0.5,
                        fontSize: '2rem',
                        color:
                          theme.palette.mode === 'dark'
                            ? colors.grey[200]
                            : colors.grey[900],
                        fontWeight: 600,
                        fontStyle: 'italic',
                      }}
                    >
                      {availablePetSizes[0].size}
                    </Typography>
                    {errors.petSizeId && (
                      <Typography
                        sx={{
                          color: 'error.main',
                          fontSize: '0.75rem',
                          mt: 0.5,
                          ml: 1.75,
                        }}
                      >
                        {errors.petSizeId}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <CustomSelect
                    value={formData.petSizeId}
                    onChange={handlePetSizeChange}
                    ariaLabel="Size"
                    error={Boolean(errors.petSizeId)}
                    helperText={errors.petSizeId}
                    placeholder="Select pet size"
                    required
                    disabled={isSubmitting || !formData.petTypeId}
                    width="100%"
                    maxWidth="600px"
                    ref={registerRef('petSizeId')}
                    onKeyDown={getKeyHandler('petSizeId')}
                  >
                    {sortPetSizesBySize(availablePetSizes).map((size) => (
                      <CustomSelectMenuItem
                        key={size.id}
                        value={size.id.toString()}
                      >
                        {size.size}
                      </CustomSelectMenuItem>
                    ))}
                  </CustomSelect>
                )}
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>Breed</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box>
                <CustomSelect
                  value={formData.petBreedId}
                  onChange={(value) => {
                    if (value === BREED_SHOW_ALL_TOGGLE_VALUE) {
                      handleToggleShowAllBreeds();
                      return;
                    }
                    handleInputChange('petBreedId', value);
                  }}
                  ariaLabel="Breed"
                  error={Boolean(errors.petBreedId)}
                  helperText={errors.petBreedId}
                  placeholder="Select breed (optional)"
                  disabled={isSubmitting || !formData.petSizeId}
                  width="100%"
                  maxWidth="600px"
                  ref={registerRef('petBreedId')}
                  onKeyDown={getKeyHandler('petBreedId')}
                >
                  {availableBreeds.map((breed) => (
                    <CustomSelectMenuItem
                      key={breed.id}
                      value={breed.id.toString()}
                    >
                      {breed.breed}
                    </CustomSelectMenuItem>
                  ))}
                  <CustomSelectMenuItem
                    value={BREED_SHOW_ALL_TOGGLE_VALUE}
                    disabled={isLoadingBreedToggle}
                  >
                    {showAllBreeds ? 'Show Relevant Breeds' : 'Show All Breeds'}
                  </CustomSelectMenuItem>
                </CustomSelect>
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>
                Sex<span className="required">*</span>
              </FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box>
                <RadioButtonGroup
                  value={formData.gender}
                  onChange={(value) => handleInputChange('gender', value)}
                  options={[
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' },
                  ]}
                  error={Boolean(errors.gender)}
                  helperText={errors.gender}
                  disabled={isSubmitting}
                  direction="horizontal"
                  name="pet-gender"
                  required
                  inputRef={registerRef('gender')}
                  onKeyDown={getKeyHandler('gender')}
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>Colour</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <CustomSelect
                value={formData.petColourId}
                onChange={(value) => {
                  if (value === COLOUR_SHOW_ALL_TOGGLE_VALUE) {
                    handleToggleShowAllColours();
                    return;
                  }
                  handleInputChange('petColourId', value);
                }}
                ariaLabel="Colour"
                placeholder="Select colour"
                disabled={isSubmitting || !formData.petTypeId}
                width="100%"
                maxWidth="600px"
                ref={registerRef('petColourId')}
                onKeyDown={getKeyHandler('petColourId')}
              >
                <CustomSelectMenuItem value="">None</CustomSelectMenuItem>
                {availableColours.map((colour) => (
                  <CustomSelectMenuItem
                    key={colour.id}
                    value={colour.id.toString()}
                  >
                    {colour.colour}
                  </CustomSelectMenuItem>
                ))}
                <CustomSelectMenuItem
                  value={COLOUR_SHOW_ALL_TOGGLE_VALUE}
                  disabled={isLoadingColourToggle}
                >
                  {showAllColours
                    ? 'Show Relevant Colours'
                    : 'Show All Colours'}
                </CustomSelectMenuItem>
              </CustomSelect>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>Date of Birth</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <DateRangePicker
                mode="single"
                desktopType="modal"
                selectedDate={formData.dateOfBirth}
                onDateChange={(value) =>
                  handleInputChange('dateOfBirth', value)
                }
                disabled={isSubmitting}
              />
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel>Inoc. Date</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <DateRangePicker
                selectedDate={formData.inoculationDate}
                mode="single"
                desktopType="modal"
                onDateChange={(value) =>
                  handleInputChange('inoculationDate', value)
                }
                disabled={isSubmitting}
              />
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="pet-sterilised-label">Sterilised</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box
                display="flex"
                alignItems="center"
                onKeyDown={getKeyHandler('sterilised')}
              >
                <StyledSwitch
                  id="pet-sterilised"
                  inputProps={{ 'aria-labelledby': 'pet-sterilised-label' }}
                  checked={formData.sterilised}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('sterilised', e.target.checked)
                  }
                  disabled={isSubmitting}
                  showLabels={true}
                  trueLabel="Yes"
                  falseLabel="No"
                  inputRef={registerRef('sterilised')}
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="pet-stillOwned-label">Still Owned</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box
                display="flex"
                alignItems="center"
                onKeyDown={getKeyHandler('stillOwned')}
              >
                <StyledSwitch
                  id="pet-stillOwned"
                  inputProps={{ 'aria-labelledby': 'pet-stillOwned-label' }}
                  checked={formData.stillOwned}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('stillOwned', e.target.checked)
                  }
                  disabled={isSubmitting}
                  showLabels={true}
                  trueLabel="Yes"
                  falseLabel="No"
                  inputRef={registerRef('stillOwned')}
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainer>
              <FieldLabel id="pet-showWarning-label">Show Warning</FieldLabel>
            </FieldLabelContainer>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <Box
                display="flex"
                alignItems="center"
                onKeyDown={getKeyHandler('showWarning')}
              >
                <StyledSwitch
                  id="pet-showWarning"
                  inputProps={{ 'aria-labelledby': 'pet-showWarning-label' }}
                  checked={formData.showWarning}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('showWarning', e.target.checked)
                  }
                  disabled={isSubmitting}
                  showLabels={true}
                  trueLabel="Yes"
                  falseLabel="No"
                  inputRef={registerRef('showWarning')}
                />
              </Box>
            </FieldInputContainer>
          </FormFieldRow>

          <FormFieldRow>
            <FieldLabelContainerMultiline>
              <FieldLabel id="pet-notes-label">
                Notes
                {formData.showWarning && <span className="required">*</span>}
              </FieldLabel>
            </FieldLabelContainerMultiline>
            <FieldInputContainer
              sx={{ paddingLeft: '1rem', maxWidth: '600px' }}
            >
              <StyledMultilineTextField
                id="pet-notes"
                inputProps={{ 'aria-labelledby': 'pet-notes-label' }}
                multiline
                rows={3}
                fullWidth
                value={formData.notes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('notes', e.target.value)
                }
                disabled={isSubmitting}
                error={Boolean(errors.notes)}
                helperText={errors.notes}
                inputRef={registerRef('notes')}
                onKeyDown={getKeyHandler('notes')}
              />
            </FieldInputContainer>
          </FormFieldRow>

          {/* Save error */}
          {submitError && (
            <FormFieldRow>
              <FieldLabelContainer>
                <FieldLabel></FieldLabel>
              </FieldLabelContainer>
              <FieldInputContainer paddingLeft={'.5rem'}>
                <Box
                  sx={{
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    border: `1px solid ${theme.palette.secondary.main}`,
                    borderRadius: '4px',
                    color: theme.palette.secondary.main,
                    fontSize: '2rem',
                    lineHeight: '1.5',
                    maxWidth: '600px',
                  }}
                >
                  <strong>{isEditMode ? 'Save' : 'Create'} Failed:</strong>{' '}
                  {submitError}
                </Box>
              </FieldInputContainer>
            </FormFieldRow>
          )}

          {isEditMode && pet && (
            <DangerZoneRow
              label={`Delete ${pet.name} ${client.lastName}`}
              onClick={() => setShowDeleteDialog(true)}
              disabled={isSubmitting}
            />
          )}

          <BottomSpacerRow datesSelected={true} />
        </ResponsiveFormGrid>
      </FormContent>

      <StickyFooter>
        <StickyButtonContainer>
          <CancelFormButton
            onClick={onCancel}
            disabled={isSubmitting}
            minWidth="210px"
            sx={{
              minWidth: { xs: '165px', sm: '210px' },
            }}
          />
          <SaveFormButton
            onClick={handleSubmit}
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            text={isEditMode ? 'Save' : 'Create'}
            minWidth="278px"
            sx={{
              minWidth: { xs: '165px', sm: '278px' },
            }}
            ref={registerRef('submit')}
            onKeyDown={getKeyHandler('submit')}
          />
        </StickyButtonContainer>
      </StickyFooter>

      <SimilarityDialog
        open={showPetSimilarityDialog}
        entityType="pet"
        similarEntity={similarPet}
        isEditMode={isEditMode}
        onUseExisting={handleUseExistingPet}
        onCreateNew={handleCreateNewPet}
        onClose={() => setShowPetSimilarityDialog(false)}
      />

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeletePet}
        isDeleting={isDeleting}
        title="Delete Pet"
        message={
          <>
            This will delete <strong>{pet?.name}</strong>. The pet will be
            removed from view but existing bookings and invoices will not be
            affected. This action cannot be undone from here.
          </>
        }
      />
    </FormWrapper>
  );
};

export default PetForm;
